import { useEffect } from 'react';
import { useDashboard } from '@/hooks/useDashboard';
import { useAuth } from '@/hooks/useAuth';
import { formatCurrency, getPercentage, cn } from '@/lib/utils';
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

export default function DashboardPage() {
  const { user } = useAuth();
  const { summary, loading, error, fetchSummary } = useDashboard();

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  if (loading && !summary) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <div className="text-center">
          <p className="text-accent-red">{error}</p>
          <button onClick={fetchSummary} className="btn-primary mt-4">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Budget alerts
  const budgetAlerts = (summary?.budgetProgress ?? []).filter((b) => {
    const pct = getPercentage(b.spent, b.amount);
    return pct >= 80;
  });

  return (
    <div className="animate-fadeIn space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">
          Welcome back, {user?.name || 'there'}
        </h1>
        <p className="mt-1 text-gray-400">
          Here's your financial overview
        </p>
      </div>

      {/* Budget Alerts */}
      {budgetAlerts.length > 0 && (
        <div className="space-y-2">
          {budgetAlerts.map((b) => {
            const pct = getPercentage(b.spent, b.amount);
            const isOver = b.spent > b.amount;
            return (
              <div
                key={b.id}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-4 py-3 text-sm',
                  isOver
                    ? 'bg-accent-red/10 text-accent-red'
                    : 'bg-accent-yellow/10 text-accent-yellow'
                )}
              >
                <AlertTriangle size={16} />
                <span>
                  <strong>{b.categoryName}</strong>
                  {isOver
                    ? ` is over budget by ${formatCurrency(b.spent - b.amount)}`
                    : ` is at ${pct}% of budget (${formatCurrency(b.amount - b.spent)} remaining)`}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div className="card animate-slideUp">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Total Income</p>
              <p className="mt-1 text-2xl font-bold text-accent-green">
                {formatCurrency(summary?.totalIncome ?? 0)}
              </p>
            </div>
            <div className="rounded-lg bg-accent-green/10 p-3">
              <TrendingUp className="h-6 w-6 text-accent-green" />
            </div>
          </div>
        </div>

        <div className="card animate-slideUp" style={{ animationDelay: '50ms' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Total Expenses</p>
              <p className="mt-1 text-2xl font-bold text-accent-red">
                {formatCurrency(summary?.totalExpenses ?? 0)}
              </p>
            </div>
            <div className="rounded-lg bg-accent-red/10 p-3">
              <TrendingDown className="h-6 w-6 text-accent-red" />
            </div>
          </div>
        </div>

        <div className="card animate-slideUp" style={{ animationDelay: '100ms' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Balance</p>
              <p className="mt-1 text-2xl font-bold text-white">
                {formatCurrency(summary?.balance ?? 0)}
              </p>
            </div>
            <div className="rounded-lg bg-brand-500/10 p-3">
              <Wallet className="h-6 w-6 text-brand-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Monthly Breakdown Chart */}
        <div className="card">
          <h2 className="mb-4 text-lg font-semibold text-white">
            Monthly Breakdown
          </h2>
          <div className="h-64">
            {summary?.monthlyBreakdown && summary.monthlyBreakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={summary.monthlyBreakdown}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3a" />
                  <XAxis dataKey="month" stroke="#6b7280" fontSize={12} />
                  <YAxis stroke="#6b7280" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1a1a25',
                      border: '1px solid #2a2a3a',
                      borderRadius: '8px',
                      color: '#fff',
                    }}
                  />
                  <Bar dataKey="income" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="expense" fill="#ef4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center text-gray-500">
                No data available
              </div>
            )}
          </div>
        </div>

        {/* Category Spending Pie Chart */}
        <div className="card">
          <h2 className="mb-4 text-lg font-semibold text-white">
            Spending by Category
          </h2>
          <div className="h-64">
            {summary?.categorySpending &&
            summary.categorySpending.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={summary.categorySpending}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="spent"
                    nameKey="categoryName"
                  >
                    {summary.categorySpending.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.categoryColor || '#6366f1'}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1a1a25',
                      border: '1px solid #2a2a3a',
                      borderRadius: '8px',
                      color: '#fff',
                    }}
                    formatter={(value: number) => formatCurrency(value)}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center text-gray-500">
                No data available
              </div>
            )}
          </div>
          {summary?.categorySpending && summary.categorySpending.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-3">
              {summary.categorySpending.map((cat) => (
                <div key={cat.categoryId} className="flex items-center gap-2">
                  <div
                    className="h-3 w-3 rounded-full"
                    style={{ backgroundColor: cat.categoryColor || '#6366f1' }}
                  />
                  <span className="text-xs text-gray-400">
                    {cat.categoryName}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent Transactions */}
        <div className="card">
          <h2 className="mb-4 text-lg font-semibold text-white">
            Recent Transactions
          </h2>
          <div className="space-y-3">
            {summary?.recentTransactions &&
            summary.recentTransactions.length > 0 ? (
              summary.recentTransactions.slice(0, 5).map((tx) => (
                <div
                  key={tx.id}
                  className="flex items-center justify-between rounded-lg bg-surface-tertiary p-3"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`rounded-lg p-2 ${
                        tx.type === 'income'
                          ? 'bg-accent-green/10'
                          : 'bg-accent-red/10'
                      }`}
                    >
                      {tx.type === 'income' ? (
                        <ArrowUpRight className="h-4 w-4 text-accent-green" />
                      ) : (
                        <ArrowDownRight className="h-4 w-4 text-accent-red" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">
                        {tx.description}
                      </p>
                      <p className="text-xs text-gray-500">
                        {tx.categoryName || 'Uncategorized'}
                      </p>
                    </div>
                  </div>
                  <p
                    className={`text-sm font-semibold ${
                      tx.type === 'income'
                        ? 'text-accent-green'
                        : 'text-accent-red'
                    }`}
                  >
                    {tx.type === 'income' ? '+' : '-'}
                    {formatCurrency(tx.amount)}
                  </p>
                </div>
              ))
            ) : (
              <p className="py-8 text-center text-sm text-gray-500">
                No recent transactions
              </p>
            )}
          </div>
        </div>

        {/* Budget Progress */}
        <div className="card">
          <h2 className="mb-4 text-lg font-semibold text-white">
            Budget Progress
          </h2>
          <div className="space-y-4">
            {summary?.budgetProgress && summary.budgetProgress.length > 0 ? (
              summary.budgetProgress.map((budget) => {
                const percentage = getPercentage(budget.spent, budget.amount);
                const isOverBudget = budget.spent > budget.amount;
                const isApproaching = percentage >= 80 && !isOverBudget;
                return (
                  <div key={budget.id}>
                    <div className="mb-1 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {budget.categoryColor && (
                          <div
                            className="h-2 w-2 rounded-full"
                            style={{ backgroundColor: budget.categoryColor }}
                          />
                        )}
                        <span className="text-sm font-medium text-gray-300">
                          {budget.categoryName || 'Unknown'}
                        </span>
                        {(isOverBudget || isApproaching) && (
                          <AlertTriangle
                            size={14}
                            className={
                              isOverBudget
                                ? 'text-accent-red'
                                : 'text-accent-yellow'
                            }
                          />
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {formatCurrency(budget.spent)} /{' '}
                        {formatCurrency(budget.amount)}
                      </span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-surface-tertiary">
                      <div
                        className={cn(
                          'h-full rounded-full transition-all duration-500',
                          isOverBudget
                            ? 'bg-accent-red'
                            : isApproaching
                              ? 'bg-accent-yellow'
                              : 'bg-brand-500'
                        )}
                        style={{ width: `${Math.min(percentage, 100)}%` }}
                      />
                    </div>
                    {isOverBudget && (
                      <p className="mt-1 text-xs text-accent-red">
                        Over budget by{' '}
                        {formatCurrency(budget.spent - budget.amount)}
                      </p>
                    )}
                    {isApproaching && (
                      <p className="mt-1 text-xs text-accent-yellow">
                        {percentage}% of budget used
                      </p>
                    )}
                  </div>
                );
              })
            ) : (
              <p className="py-8 text-center text-sm text-gray-500">
                No budgets set
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
